import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ArticlesService } from '../api/services/articles.service';
import { SystemsService } from '../api/services/systems.service';
import { ArticleModel, ArticleModelPagedResponseModel, SystemModel, SystemModelPagedResponseModel, QueryParams } from '../api/models';
import { AuthService } from '../shared/auth-service/auth.service';
import { AppToastService } from '../shared/toast-service/toast-service';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, firstValueFrom } from 'rxjs';
import { of, Observable } from 'rxjs';
import { Article } from '../shared/models/article.interface';
import { ArticleService } from '../articles/services/article.service';

// Make Math available in template
declare var Math: any;

@Component({
    selector: 'app-articles-management',
    templateUrl: './articles-management.component.html',
    styleUrls: ['./articles-management.component.scss'],
    providers: [DatePipe],
    standalone: false,
})
export class ArticlesManagementComponent implements OnInit {
    public loggedIn!: boolean;
    title = 'Articles Management';
    articles: ArticleModel[] = [];
    filteredArticles: ArticleModel[] = [];
    systems: SystemModel[] = [];
    loading = false;
    searchTerm = '';
    selectedSystem = '';
    modalContent: ArticleModel | undefined;
    selectedSystemForArticle: string = '';
    displayError = false;
    errorMessage = '';

    // Pagination
    currentPage = 1;
    itemsPerPage = 50;
    totalItems = 0;

    // Filter options
    filterOptions = [
        { label: 'Aluminum', selected: false },
        { label: 'Steel', selected: false },
        { label: 'Composite', selected: false }
    ];

    // Sorting
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    constructor(
        private articlesService: ArticlesService,
        private systemsService: SystemsService,
        private modalService: NgbModal,
        private authService: AuthService,
        private toastService: AppToastService,
        private datePipe: DatePipe,
        private router: Router,
        private articleService: ArticleService
    ) { }

    ngOnInit(): void {
        this.loggedIn = this.isLoggedIn();
        this.loadArticles();
        this.loadSystems();
    }

    private isLoggedIn(): boolean {
        return !!this.authService.getToken();
    }

    public logout(): void {
        this.authService.logout();
    }

    loadArticles(): void {
        this.loading = true;
        const params: QueryParams = {
            page: this.currentPage,
            pageSize: this.itemsPerPage
        };

        // Use different API method based on system filter
        const apiCall = this.selectedSystem
            ? this.articlesService.getArticlesBySystem({ name: this.selectedSystem }, params)
            : this.articlesService.getArticles(params);

        apiCall.subscribe({
            next: (response: ArticleModelPagedResponseModel | any) => {
                // Handle both camelCase (new) and PascalCase (old) formats
                const data = response.data || response.Data || [];
                // Transform articles from PascalCase to camelCase if needed
                this.articles = Array.isArray(data) 
                    ? data.map(article => this.transformArticleIfNeeded(article))
                    : [];
                this.filteredArticles = [...this.articles];
                // Use totalCount first, then fallback to pagination.totalItems
                // Handle both camelCase and PascalCase
                this.totalItems = response.totalCount || response.TotalCount || 
                                 response.pagination?.totalItems || 
                                 response.Pagination?.TotalItems || 0;
                this.loading = false;

                // Apply additional filters if any are selected
                this.applyFilters();
            },
            error: (error) => {
                console.error('Error loading articles:', error);
                this.showError('Could not load articles.');
                this.loading = false;
            }
        });
    }

    loadSystems(): void {
        // Get all systems without pagination for the dropdown
        this.systemsService.getSystems({ page: 1, pageSize: 1000 }).subscribe({
            next: (response: SystemModelPagedResponseModel | any) => {
                // Handle both camelCase (new) and PascalCase (old) formats
                // The service already unwraps ApiResponse, so response is SystemModelPagedResponseModel
                const data = response.data || response.Data || [];
                // Transform systems from PascalCase to camelCase if needed
                this.systems = Array.isArray(data) 
                    ? data.map(system => this.transformSystemIfNeeded(system))
                    : [];
            },
            error: (error) => {
                console.error('Error loading systems:', error);
            }
        });
    }


    getSystemNameForArticle(articleName: string): string {
        // Find the article first
        const article = this.articles.find(a => {
            const name = a.articleName || '';
            return name === articleName;
        });
        if (!article) return 'No System';

        // Check if article has systems directly
        if (article.systems && article.systems.length > 0) {
            const system = article.systems[0];
            return system.name || 'Unknown System';
        }

        // Fallback to checking by systemIds
        if (article.systemIds && article.systemIds.length > 0) {
            const system = this.systems.find(s => {
                const systemId = s.id || '';
                return systemId === article.systemIds![0];
            });
            return system?.name || 'Unknown System';
        }

        return 'No System';
    }

    search(event: Event): void {
        event.preventDefault();
        // Apply search filter to current articles
        this.applyFilters();
    }

    onSystemFilterChange(value?: string): void {
        // Update selectedSystem if value is provided
        if (value !== undefined) {
            this.selectedSystem = value;
        }
        this.currentPage = 1; // Reset to first page when filtering
        this.loadArticles(); // Reload articles with system filter
    }

    onFilterChange(filter: any, event: Event): void {
        filter.selected = (event.target as HTMLInputElement).checked;
        this.applyFilters();
    }

    removeFilter(filter: any): void {
        filter.selected = false;
        this.applyFilters();
    }

    clearAllFilters(): void {
        this.selectedSystem = '';
        this.searchTerm = '';
        this.filterOptions.forEach(filter => filter.selected = false);
        this.currentPage = 1;
        this.loadArticles();
    }

    applyFilters(): void {
        // Start with all articles
        let filtered = [...this.articles];

        // Apply material filters if any are selected
        const selectedFilters = this.filterOptions.filter(f => f.selected).map(f => f.label);
        if (selectedFilters.length > 0) {
            filtered = filtered.filter(article => {
                const material = article.material || '';
                return selectedFilters.some(filter =>
                    material.toLowerCase().includes(filter.toLowerCase())
                );
            });
        }

        // Apply search filter if there's a search term
        if (this.searchTerm && this.searchTerm.trim()) {
            const searchLower = this.searchTerm.toLowerCase().trim();
            filtered = filtered.filter(article => {
                const articleName = (article.articleName || '').toLowerCase();
                const material = (article.material || '').toLowerCase();
                const type = (article.type || '').toLowerCase();
                return articleName.includes(searchLower) ||
                       material.includes(searchLower) ||
                       type.includes(searchLower);
            });
        }

        this.filteredArticles = filtered;
        this.applySorting();
    }

    onSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.applySorting();
    }

    applySorting(): void {
        if (!this.sortColumn) {
            return;
        }

        this.filteredArticles.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (this.sortColumn) {
                case 'articleName':
                    aValue = (a.articleName || '').toLowerCase();
                    bValue = (b.articleName || '').toLowerCase();
                    break;
                case 'material':
                    aValue = (a.material || '').toLowerCase();
                    bValue = (b.material || '').toLowerCase();
                    break;
                case 'type':
                    aValue = (a.type || '').toLowerCase();
                    bValue = (b.type || '').toLowerCase();
                    break;
                case 'system':
                    aValue = this.getSystemNameForArticle(a.articleName || '').toLowerCase();
                    bValue = this.getSystemNameForArticle(b.articleName || '').toLowerCase();
                    break;
                case 'createdAt':
                    aValue = new Date(a.createdAt || 0).getTime();
                    bValue = new Date(b.createdAt || 0).getTime();
                    break;
                case 'updatedAt':
                    aValue = new Date(a.updatedAt || 0).getTime();
                    bValue = new Date(b.updatedAt || 0).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return this.sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return this.sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    getSortIcon(column: string): string {
        if (this.sortColumn !== column) {
            return 'bi-arrow-down-up';
        }
        return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
    }

    getSelectedFilters(): any[] {
        return this.filterOptions.filter(filter => filter.selected);
    }

    openArticleDetailModal(modal: any, article: ArticleModel): void {
        // Ensure article is transformed before displaying
        this.modalContent = this.transformArticleIfNeeded(article);
        this.modalService.open(modal, {
            ariaLabelledBy: 'modal-basic-title',
            windowClass: 'fluid-modal',
        });
    }

    openCreateArticleModal(modal: any): void {
        this.modalContent = {
            articleName: '',
            material: '',
            type: ''
        } as ArticleModel;
        this.modalService.open(modal, {
            ariaLabelledBy: 'modal-basic-title',
            windowClass: 'fluid-modal',
        });
    }

    openEditArticleModal(modal: any, article: ArticleModel): void {
        // Ensure article is transformed before editing
        this.modalContent = { ...this.transformArticleIfNeeded(article) };
        this.modalService.open(modal, {
            ariaLabelledBy: 'modal-basic-title',
            windowClass: 'fluid-modal',
        });
    }

    saveArticle(article: ArticleModel): void {
        this.loading = true;
        // Ensure article is transformed before saving
        const transformedArticle = this.transformArticleIfNeeded(article);
        if (transformedArticle.id) {
            // Update existing article
            this.articlesService.updateArticle(transformedArticle.id, transformedArticle).subscribe({
                next: (response) => {
                    this.toastService.show('Article updated successfully', {
                        classname: 'bg-success text-light',
                        delay: 4000
                    });
                    this.loadArticles();
                    this.modalService.dismissAll();
                },
                error: (error) => {
                    console.error('Error updating article:', error);
                    this.showError('Could not update article.');
                    this.loading = false;
                }
            });
        } else {
            // Create new article
            // If a system is selected, add it to the article's SystemIds
            if (this.selectedSystemForArticle) {
                transformedArticle.systemIds = [this.selectedSystemForArticle];
            }

            this.articlesService.createArticle(transformedArticle).subscribe({
                next: (response) => {
                    this.toastService.show('Article created successfully', {
                        classname: 'bg-success text-light',
                        delay: 4000
                    });
                    this.loadArticles();
                    this.modalService.dismissAll();
                },
                error: (error) => {
                    console.error('Error creating article:', error);
                    this.showError('Could not create article.');
                    this.loading = false;
                }
            });
        }
    }


    deleteArticle(article: ArticleModel): void {
        // Ensure article is transformed to get correct id and articleName
        const transformedArticle = this.transformArticleIfNeeded(article);
        if (transformedArticle.id && confirm(`Are you sure you want to delete article "${transformedArticle.articleName}"?`)) {
            this.loading = true;
            this.articlesService.deleteArticle(transformedArticle.id).subscribe({
                next: () => {
                    this.toastService.show('Article deleted successfully', {
                        classname: 'bg-success text-light',
                        delay: 4000
                    });
                    this.loadArticles();
                },
                error: (error) => {
                    console.error('Error deleting article:', error);
                    this.showError('Could not delete article.');
                    this.loading = false;
                }
            });
        }
    }

    navigateToArticleStaging(): void {
        this.router.navigate(['/articles']);
    }

    onPageChange(page: number, event?: Event): void {
        if (event) {
            event.preventDefault();
        }

        // Validate page number
        if (page < 1 || page > this.getTotalPages()) {
            console.warn('Invalid page number:', page);
            return;
        }

        this.currentPage = page;
        this.loadArticles();
    }

    showError(message: string): void {
        this.displayError = true;
        this.errorMessage = message;
    }

    formatDate(date: string | Date | undefined): string {
        return date ? this.datePipe.transform(date, 'MMM-dd-yyyy hh:mm a') || '' : '';
    }

    getTotalPages(): number {
        return Math.ceil(this.totalItems / this.itemsPerPage);
    }

    getPagesArray(): number[] {
        const totalPages = this.getTotalPages();
        const maxVisiblePages = 7; // Show max 7 page numbers

        if (totalPages <= maxVisiblePages) {
            return Array(totalPages).fill(0).map((_, i) => i + 1);
        }

        const startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        return Array(endPage - startPage + 1).fill(0).map((_, i) => startPage + i);
    }

    getItemRangeStart(): number {
        return (this.currentPage - 1) * this.itemsPerPage + 1;
    }

    getItemRangeEnd(): number {
        return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    }

    // Transform article object from PascalCase to camelCase if needed
    private transformArticleIfNeeded(article: any): ArticleModel {
        if (!article) return article;
        if (typeof article !== 'object') return article;
        
        // Check if already in camelCase (has lowercase properties)
        if (article.articleName !== undefined || article.id !== undefined) {
            // Already in camelCase, but check nested objects
            const transformed: ArticleModel = {
                ...article,
                systems: article.systems && Array.isArray(article.systems)
                    ? article.systems.map((system: any) => this.transformSystemIfNeeded(system))
                    : article.systems
            };
            return transformed;
        }
        
        // Transform from PascalCase to camelCase
        const transformed: ArticleModel = {
            id: article.Id || article.id,
            articleName: article.ArticleName || article.articleName || '',
            material: article.Material || article.material,
            type: article.Type || article.type,
            systemIds: article.SystemIds || article.systemIds,
            systems: (article.Systems || article.systems || []).map((system: any) => this.transformSystemIfNeeded(system)),
            createdAt: article.CreatedAt || article.createdAt,
            updatedAt: article.UpdatedAt || article.updatedAt
        };
        
        return transformed;
    }

    // Transform system object from PascalCase to camelCase if needed
    private transformSystemIfNeeded(system: any): SystemModel {
        if (!system) return system;
        if (typeof system !== 'object') return system;
        
        // Check if already in camelCase (has lowercase properties)
        if (system.name !== undefined || system.id !== undefined) {
            // Already in camelCase, return as is
            return system;
        }
        
        // Transform from PascalCase to camelCase
        const transformed: SystemModel = {
            id: system.Id || system.id,
            name: system.Name || system.name || '',
            version: system.Version || system.version,
            description: system.Description || system.description,
            material: system.Material || system.material,
            type: system.Type || system.type,
            createdAt: system.CreatedAt || system.createdAt,
            updatedAt: system.UpdatedAt || system.updatedAt
        };
        
        return transformed;
    }

    navigateToArticleViewer(article: ArticleModel): void {
        console.log('Navigating to article viewer for article:', article);
        
        // Get the full article data with ThreeJSFileStoragePath using the same approach as articles component
        this.articleService.searchArticlesOrSystems(article.articleName).subscribe({
            next: (searchResult: any) => {
                console.log('Search result from article service:', searchResult);
                console.log('Search result type:', typeof searchResult);
                console.log('Search result keys:', Object.keys(searchResult || {}));
                
                // Handle the nested structure returned by searchArticlesOrSystems
                let articles: Article[] = [];
                
                if (searchResult && searchResult.systemNames && Array.isArray(searchResult.systemNames)) {
                    console.log('Found systemNames array with', searchResult.systemNames.length, 'items');
                    // Extract articles from all systems
                    searchResult.systemNames.forEach((system: any, index: number) => {
                        console.log(`System ${index}:`, system);
                        console.log(`System ${index} articles:`, system.articles);
                        if (system.articles && Array.isArray(system.articles)) {
                            console.log(`Adding ${system.articles.length} articles from system ${index}`);
                            articles = articles.concat(system.articles);
                        }
                    });
                } else if (searchResult && searchResult.systems && Array.isArray(searchResult.systems)) {
                    console.log('Found systems array with', searchResult.systems.length, 'items');
                    // Extract articles from all systems
                    searchResult.systems.forEach((system: any, index: number) => {
                        console.log(`System ${index}:`, system);
                        console.log(`System ${index} articles:`, system.articles);
                        if (system.articles && Array.isArray(system.articles)) {
                            console.log(`Adding ${system.articles.length} articles from system ${index}`);
                            articles = articles.concat(system.articles);
                        }
                    });
                } else if (Array.isArray(searchResult)) {
                    console.log('Search result is already an array with', searchResult.length, 'items');
                    // Check if this is the systemNames structure
                    if (searchResult.length > 0 && searchResult[0].systemNames) {
                        console.log('Found systemNames structure in array, extracting articles...');
                        searchResult.forEach((item: any) => {
                            if (item.systemNames && Array.isArray(item.systemNames)) {
                                item.systemNames.forEach((system: any) => {
                                    if (system.articles && Array.isArray(system.articles)) {
                                        articles = articles.concat(system.articles);
                                    }
                                });
                            }
                        });
                    } else if (searchResult.length > 0 && searchResult[0].systems) {
                        console.log('Found systems structure in array, extracting articles...');
                        searchResult.forEach((item: any) => {
                            if (item.systems && Array.isArray(item.systems)) {
                                item.systems.forEach((system: any) => {
                                    if (system.articles && Array.isArray(system.articles)) {
                                        articles = articles.concat(system.articles);
                                    }
                                });
                            }
                        });
                    } else {
                        // Fallback: if it's already an array of articles
                        articles = searchResult;
                    }
                } else {
                    console.log('Unexpected search result structure. Trying to find articles in different ways...');
                    
                    // Try different possible structures
                    if (searchResult && searchResult.data && Array.isArray(searchResult.data)) {
                        console.log('Found articles in searchResult.data');
                        articles = searchResult.data;
                    } else if (searchResult && searchResult.articles && Array.isArray(searchResult.articles)) {
                        console.log('Found articles in searchResult.articles');
                        articles = searchResult.articles;
                    } else if (searchResult && typeof searchResult === 'object') {
                        console.log('Searching for articles in all object properties...');
                        Object.values(searchResult).forEach((value: any) => {
                            if (Array.isArray(value)) {
                                // Check if this array contains systemNames structure
                                if (value.length > 0 && value[0].systemNames) {
                                    console.log('Found systemNames in object property array');
                                    value.forEach((item: any) => {
                                        if (item.systemNames && Array.isArray(item.systemNames)) {
                                            item.systemNames.forEach((system: any) => {
                                                if (system.articles && Array.isArray(system.articles)) {
                                                    articles = articles.concat(system.articles);
                                                }
                                            });
                                        }
                                    });
                                } else if (value.length > 0 && value[0].systems) {
                                    console.log('Found systems in object property array');
                                    value.forEach((item: any) => {
                                        if (item.systems && Array.isArray(item.systems)) {
                                            item.systems.forEach((system: any) => {
                                                if (system.articles && Array.isArray(system.articles)) {
                                                    articles = articles.concat(system.articles);
                                                }
                                            });
                                        }
                                    });
                                } else if (value.length > 0 && value[0].ArticleName) {
                                    console.log('Found articles array in object property:', value);
                                    articles = articles.concat(value);
                                }
                            }
                        });
                    }
                }
                
                console.log('Extracted articles array:', articles);
                console.log('Extracted articles array length:', articles.length);
                
                if (!articles || articles.length === 0) {
                    console.log('No articles found, showing error');
                    this.showError(`Article "${article.articleName}" not found in staging system.`);
                    return;
                }
                
                // Find the matching article
                console.log('Looking for article with name:', article.articleName);
                console.log('Available article names in array:');
                articles.forEach((articleItem, index) => {
                    console.log(`  Article ${index}: "${articleItem.ArticleName}" (type: ${typeof articleItem.ArticleName})`);
                });
                
                const fullArticle = articles.find(articleItem => {
                    console.log(`Comparing: "${articleItem.ArticleName}" with "${article.articleName}"`);
                    console.log(`Types: ${typeof articleItem.ArticleName} vs ${typeof article.articleName}`);
                    console.log(`Trimmed comparison: "${articleItem.ArticleName?.trim()}" vs "${article.articleName?.trim()}"`);
                    return articleItem.ArticleName === article.articleName;
                });
                
                console.log('Full article data retrieved for viewer navigation:', fullArticle);
                
                if (!fullArticle) {
                    console.log('Article not found. Trying case-insensitive and trimmed comparison...');
                    const fallbackArticle = articles.find(articleItem => 
                        articleItem.ArticleName?.trim().toLowerCase() === article.articleName?.trim().toLowerCase()
                    );
                    console.log('Fallback article found:', fallbackArticle);
                    
                    if (fallbackArticle) {
                        console.log('Using fallback article for navigation');
                        // Use the fallback article for the rest of the logic
                        // Check if the article has ThreeJSFileStoragePath
                        if (!fallbackArticle.ThreeJSFileStoragePath || fallbackArticle.ThreeJSFileStoragePath.trim() === '') {
                            this.showError(`Article "${fallbackArticle.ArticleName}" does not have a 3D model file (ThreeJSFileStoragePath is empty).`);
                            
                            // Also show toast notification
                            this.toastService.show(`Article "${fallbackArticle.ArticleName}" does not have a 3D model file.`, { 
                                classname: 'bg-danger text-light', 
                                delay: 5000,
                                autohide: true 
                            });
                            return;
                        }
                        
                        // Proceed with the same logic as articles component
                        this.correctUrl(fallbackArticle.ThreeJSFileStoragePath, fallbackArticle.ArticleName).subscribe({
                            next: (correctedUrl) => {
                                console.log('Corrected URL:', correctedUrl);
                                
                                // Store data in sessionStorage for cross-tab access
                                sessionStorage.setItem('articleViewerData', JSON.stringify([correctedUrl]));
                                
                                // Open in new tab
                                window.open('/article-viewer', '_blank');
                            },
                            error: (error) => {
                                console.error('Error correcting URL:', error);
                                this.showError('Failed to process article URL. Please try again.');
                            }
                        });
                    } else {
                        this.showError(`Article "${article.articleName}" not found in staging system.`);
                    }
                    return;
                }
                
                // Check if the article has ThreeJSFileStoragePath
                if (!fullArticle.ThreeJSFileStoragePath || fullArticle.ThreeJSFileStoragePath.trim() === '') {
                    this.showError(`Article "${fullArticle.ArticleName}" does not have a 3D model file (ThreeJSFileStoragePath is empty).`);
                    
                    // Also show toast notification
                    this.toastService.show(`Article "${fullArticle.ArticleName}" does not have a 3D model file.`, { 
                        classname: 'bg-danger text-light', 
                        delay: 5000,
                        autohide: true 
                    });
                    return;
                }
                
                // Proceed with the same logic as articles component
                this.correctUrl(fullArticle.ThreeJSFileStoragePath, fullArticle.ArticleName).subscribe({
                    next: (correctedUrl) => {
                        console.log('Corrected URL:', correctedUrl);
                        
                        // Store data in sessionStorage for cross-tab access
                        sessionStorage.setItem('articleViewerData', JSON.stringify([correctedUrl]));
                        
                        // Open in new tab
                        window.open('/article-viewer', '_blank');
                    },
                    error: (error) => {
                        console.error('Error correcting URL:', error);
                        this.showError('Failed to process article URL. Please try again.');
                    }
                });
            },
            error: (error) => {
                console.error('Error searching for article:', error);
                this.showError('Failed to find article in staging system. Please try again.');
            }
        });
    }

    correctUrl(url: string | undefined, articleName: string): Observable<string> {
        const oldBucketPrefix = 'https://article-data-files.s3.amazonaws.com/';
        const newBucketPrefix = 'https://article-data-files-dev.s3.amazonaws.com/';
        
        if (url && url.startsWith(oldBucketPrefix)) {
            url = url.replace(oldBucketPrefix, newBucketPrefix);
        }
        const incorrectPrefix = "https://s3-.amazonaws.com/article-data-files/article-3js-files";
        const correctPrefix = "https://article-data-files-dev.s3.amazonaws.com/article-3js-files";

        if (url && url.includes(incorrectPrefix)) {
            url = url.replace(incorrectPrefix, correctPrefix);
        }

        if (!url) {
            return of('');
        }

        const prefix = "https://article-data-files-dev.s3.amazonaws.com/article-3js-files";
        const finalUrl = this.ensurePrefixInURL(prefix, url);

        return of(finalUrl);
    }

    // Utility for `correctUrl()`.
    private ensurePrefixInURL(prefix: string, url: string): string {
        const regex = new RegExp(`^${prefix}/article__\\d{6}\\..+$`);
      
        if (!regex.test(url)) {
            return `${prefix}/article__${url}`;
        }
      
        return url;
    }
}
